import {
  Banknote,
  Bitcoin,
  ChartLine,
  ClipboardList,
  ClockArrowDown,
  FileSignature,
  FolderLock,
  HandCoins,
  KeyRound,
  ListChecks,
  LogIn,
  type LucideIcon,
  ScanFace,
  ShieldAlert,
  UserLock,
} from 'lucide-react';
import { type ComponentType, lazy } from 'react';
import type { UserTabProps, UserType } from '@/services/api/user-console';

// Lazy imports to the pinned per-tab contract paths — each tab is a DEFAULT export
// taking UserTabProps. Lazy + first-activation mounting reproduces the legacy
// per-tab-on-activate fetching with TanStack Query as the cache.
const AccessTab = lazy(() => import('./tabs/access/access-tab'));
const AssetsTab = lazy(() => import('./tabs/assets/assets-tab'));
const AuditTab = lazy(() => import('./tabs/audit/audit-tab'));
const AuthTab = lazy(() => import('./tabs/auth/auth-tab'));
const FuturesTab = lazy(() => import('./tabs/futures/futures-tab'));
const PayoutTab = lazy(() => import('./tabs/payout/payout-tab'));
const QuestionnairesTab = lazy(() => import('./tabs/questionnaires/questionnaires-tab'));
const PiiTab = lazy(() => import('./tabs/pii/pii-tab'));
const TermsTab = lazy(() => import('./tabs/terms/terms-tab'));
const TradesTab = lazy(() => import('./tabs/trades/trades-tab'));
const TransactionsTab = lazy(() => import('./tabs/transactions/transactions-tab'));
const RiskTab = lazy(() => import('./tabs/risk/risk-tab'));
const KycTab = lazy(() => import('./tabs/kyc/kyc-tab'));
const SupportingDocumentsTab = lazy(() => import('./tabs/supporting-documents/supporting-documents-tab'));

export const USER_TAB_VALUES = [
  'access',
  'assets',
  'audit',
  'auth',
  'futures',
  'payoutAccounts',
  'questionnaires',
  'pii',
  'terms',
  'trades',
  'transactions',
  'risk',
  'kyc',
  'supportingDocuments',
] as const;
export type UserTabValue = (typeof USER_TAB_VALUES)[number];

export const DEFAULT_TAB: UserTabValue = 'access';

export type UserTabGroupId = 'account' | 'funds' | 'trading' | 'compliance';

export const USER_TAB_GROUPS: readonly { id: UserTabGroupId; label: string }[] = [
  { id: 'account', label: 'Account' },
  { id: 'funds', label: 'Funds' },
  { id: 'trading', label: 'Trading' },
  { id: 'compliance', label: 'Compliance' },
];

export interface UserTabDefinition {
  value: UserTabValue;
  label: string;
  icon: LucideIcon;
  group: UserTabGroupId;
  /** user types the tab (trigger AND panel) is hidden for */
  hiddenFor?: readonly UserType[];
  /** Unmount when inactive: sensitive payloads (PII/KYC) must not sit in memory while hidden */
  sensitive?: boolean;
  Component: ComponentType<UserTabProps>;
}

// DOM and keyboard order follow this array. Only Auth and Payout Accounts hide for broker clients (PRESERVE)
export const USER_TABS: readonly UserTabDefinition[] = [
  { value: 'access', label: 'Access', icon: KeyRound, group: 'account', Component: AccessTab },
  { value: 'auth', label: 'Auth', icon: LogIn, group: 'account', hiddenFor: ['BROKER_CLIENT'], Component: AuthTab },
  { value: 'terms', label: 'Terms', icon: FileSignature, group: 'account', Component: TermsTab },
  { value: 'audit', label: 'Audit', icon: ListChecks, group: 'account', Component: AuditTab },
  { value: 'assets', label: 'Assets', icon: Bitcoin, group: 'funds', Component: AssetsTab },
  { value: 'transactions', label: 'Transactions', icon: Banknote, group: 'funds', Component: TransactionsTab },
  { value: 'payoutAccounts', label: 'Payout Accounts', icon: HandCoins, group: 'funds', hiddenFor: ['BROKER_CLIENT'], Component: PayoutTab },
  { value: 'trades', label: 'Trades', icon: ChartLine, group: 'trading', Component: TradesTab },
  { value: 'futures', label: 'Futures', icon: ClockArrowDown, group: 'trading', Component: FuturesTab },
  { value: 'risk', label: 'Risk', icon: ShieldAlert, group: 'trading', Component: RiskTab },
  { value: 'kyc', label: 'KYC', icon: ScanFace, group: 'compliance', Component: KycTab, sensitive: true },
  { value: 'questionnaires', label: 'Questionnaires', icon: ClipboardList, group: 'compliance', Component: QuestionnairesTab },
  { value: 'pii', label: 'PII', icon: UserLock, group: 'compliance', Component: PiiTab, sensitive: true },
  { value: 'supportingDocuments', label: 'Documents', icon: FolderLock, group: 'compliance', Component: SupportingDocumentsTab, sensitive: true },
];

export const visibleTabsFor = (userType: UserType): UserTabDefinition[] => USER_TABS.filter((tab) => !tab.hiddenFor?.includes(userType));

export interface UserTabGroupWithTabs {
  id: UserTabGroupId;
  label: string;
  tabs: UserTabDefinition[];
}

/** Buckets tabs into their nav groups, dropping groups left empty by visibility rules. */
export const groupTabs = (tabs: readonly UserTabDefinition[]): UserTabGroupWithTabs[] =>
  USER_TAB_GROUPS.map((group) => ({ ...group, tabs: tabs.filter((tab) => tab.group === group.id) })).filter((group) => group.tabs.length > 0);

// Legacy deep-link alias: ?tab=verification meant the KYC tab
const TAB_ALIASES: Record<string, UserTabValue> = { verification: 'kyc' };

/**
 * ?tab= is the single source of truth (deliberate improvement: persist for
 * EVERY tab, not just pii). Unknown or hidden values fall back to the default tab —
 * legacy accepted any string verbatim with no validation.
 */
export const resolveTabParam = (param: string | null, visibleValues: readonly string[]): UserTabValue => {
  if (!param) return DEFAULT_TAB;
  const mapped = TAB_ALIASES[param] ?? param;
  return visibleValues.includes(mapped) ? (mapped as UserTabValue) : DEFAULT_TAB;
};
